// Components/Banner/ViewBanner.jsx
import React, { useMemo, useCallback } from "react";
import { FaLink, FaCalendar, FaIdCard, FaInfoCircle } from 'react-icons/fa';

function ViewBanner({ data }) {
  // ✅ Proper image URL construction with memoization
  const imageUrl = useMemo(() => {
    if (!data?.image) {
      return '/placeholder-image.jpg';
    }

    // If image already has protocol (full URL)
    if (data.image.startsWith('http://') || data.image.startsWith('https://')) {
      return data.image;
    }

    // Use imageBaseUrl from data
    if (data?.imageBaseUrl) {
      const base = data.imageBaseUrl.replace(/\/$/, '');
      const image = data.image.replace(/^\//, '');
      return `${base}/${image}`;
    }

    // Fallback to environment variable
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${baseUrl}/uploads/banner/${data.image.replace(/^\//, '')}`;
  }, [data?.image, data?.imageBaseUrl]);

  // ✅ Check if status is active (handles multiple formats)
  const isActive = useCallback((status) => {
    if (status === undefined || status === null) return false;
    return status === "active" || 
           status === "Active" || 
           status === 1 || 
           status === true || 
           status === "1";
  }, []);

  // ✅ Format date safely
  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'N/A';
    }
  }, []);

  // ✅ Format status for display
  const getStatusDisplay = useCallback((status) => {
    if (isActive(status)) return 'Active';
    return 'Inactive';
  }, [isActive]);

  // ✅ Get status badge class
  const getStatusBadgeClass = useCallback((status) => {
    return isActive(status) ? 'bg-success' : 'bg-danger';
  }, [isActive]);

  // ✅ Handle image error
  const handleImageError = useCallback((e) => {
    console.warn('Failed to load banner image:', {
      image: data?.image,
      imageBaseUrl: data?.imageBaseUrl,
      constructedUrl: imageUrl
    });
    e.target.src = '/placeholder-image.jpg';
    e.target.onerror = null; // Prevent infinite loop
  }, [data?.image, data?.imageBaseUrl, imageUrl]);

  // ✅ If no data, show fallback
  if (!data) {
    return (
      <div className="custom-modal-body text-center py-5">
        <div className="alert alert-warning">
          <FaInfoCircle className="me-2" />
          No banner data available
        </div>
      </div>
    );
  }

  return (
    <div className="custom-modal-body">
      <div className="model-custom">
        {/* ✅ Image Section with proper styling */}
        <div className="mb-4">
          <div className="position-relative overflow-hidden rounded" style={{ 
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef'
          }}>
            <img
              src={imageUrl}
              alt={data?.name || "Banner"}
              className="w-100"
              style={{
                maxHeight: "200px",
                objectFit: "contain",
                transition: 'transform 0.3s ease'
              }}
              onError={handleImageError}
              loading="lazy"
            />
          </div>
        </div>

        {/* ✅ Banner Name */}
        <h3 className="mb-3 fw-semibold text-primary">
          {data?.name || 'Unnamed Banner'}
        </h3>

        {/* ✅ Description */}
        {data?.description && (
          <div className="mb-3 p-3 bg-light rounded">
            <p className="mb-0">{data.description}</p>
          </div>
        )}

        {/* ✅ Details Grid */}
        <div className="row g-3 mt-2">
          {/* Link */}
          {data?.link && (
            <div className="col-12">
              <div className="d-flex align-items-center p-2 border rounded">
                <FaLink className="text-primary me-2" size={16} />
                <strong className="me-2">Link:</strong>
                <a 
                  href={data.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary text-break"
                >
                  {data.link}
                </a>
              </div>
            </div>
          )}

          {/* Status */}
          <div className="col-6">
            <div className="p-2 border rounded">
              <strong className="d-block mb-1">Status:</strong>
              <span className={`badge ${getStatusBadgeClass(data.status)} fs-6 px-3 py-2`}>
                {getStatusDisplay(data.status)}
              </span>
            </div>
          </div>

          {/* Banner ID */}
          <div className="col-6">
            <div className="p-2 border rounded">
              <strong className="d-block mb-1">Banner ID:</strong>
              <span className="badge bg-secondary fs-6 px-3 py-2">
                <FaIdCard className="me-1" size={14} />
                #{data?.id || 'N/A'}
              </span>
            </div>
          </div>

          {/* Created At */}
          <div className="col-12">
            <div className="p-2 border rounded">
              <strong className="d-block mb-1">
                <FaCalendar className="me-2" size={14} />
                Created At:
              </strong>
              <span className="fs-6">{formatDate(data?.created_at)}</span>
            </div>
          </div>

          {/* Order/Position (if exists) */}
          {data?.order !== undefined && (
            <div className="col-6">
              <div className="p-2 border rounded">
                <strong className="d-block mb-1">Position:</strong>
                <span className="badge bg-info fs-6 px-3 py-2">
                  #{data.order}
                </span>
              </div>
            </div>
          )}

          {/* Type (if exists) */}
          {data?.type && (
            <div className="col-6">
              <div className="p-2 border rounded">
                <strong className="d-block mb-1">Type:</strong>
                <span className="badge bg-warning text-dark fs-6 px-3 py-2">
                  {data.type}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewBanner;